import { cn } from "@somethings-ui/core";
import { Pencil, Sparkles, Trash2 } from "lucide-react";
import { DateTime } from "luxon";
import { useLayoutEffect, useRef, useState } from "react";
import type { Note } from "@/lib/api/notes";
import { resolveDisplayTitle, resolveNoteSource } from "./context-note-utils";

function formatNoteTimestamp(iso: string): string {
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return "";
  const diffDays = DateTime.now().startOf("day").diff(dt.startOf("day"), "days")
    .days;
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return dt.toFormat("ccc");
  return dt.toFormat("LLL d");
}

export function ContextNoteCard({
  note,
  canEdit,
  onEdit,
  onDelete,
}: {
  note: Note;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const source = resolveNoteSource(note);
  const isAi = source === "ai";
  const Icon = isAi ? Sparkles : Pencil;
  const title = resolveDisplayTitle(note);

  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const contentRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    if (expanded) return;
    const el = contentRef.current;
    if (!el) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [note.content, expanded]);

  return (
    <div className="group flex flex-col gap-2 border-b border-surfaces-divider py-4">
      <div className="flex gap-3">
        <span className="flex size-[22px] shrink-0 items-center justify-center rounded-xl bg-surfaces-z3">
          <Icon className="size-3 text-icon-primary" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5">
          <div className="flex min-h-4 items-center justify-between gap-2">
            <span className="font-body text-body-4 text-text-tertiary">
              {isAi ? "AI" : "Documentation"}
            </span>
            <span
              className={cn(
                "font-body text-body-4 text-text-tertiary",
                canEdit && "group-hover:hidden",
              )}
            >
              {formatNoteTimestamp(note.createdAt)}
            </span>
            {canEdit ? (
              <div className="hidden items-center gap-2 group-hover:flex">
                <button
                  type="button"
                  aria-label="Edit note"
                  onClick={onEdit}
                  className="text-text-tertiary transition-colors hover:text-text-primary"
                >
                  <Pencil className="size-4" />
                </button>
                <button
                  type="button"
                  aria-label="Delete note"
                  onClick={onDelete}
                  className="text-text-tertiary transition-colors hover:text-text-primary"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            ) : null}
          </div>
          {title ? (
            <h4 className="font-avantt text-[18px] font-bold leading-[22px] text-text-primary [overflow-wrap:anywhere]">
              {title}
            </h4>
          ) : null}
          <p
            ref={contentRef}
            className={cn(
              "whitespace-pre-line break-words font-body text-body-2 text-text-secondary [overflow-wrap:anywhere]",
              title && "mt-0.5",
              !expanded && "line-clamp-6",
            )}
          >
            {note.content}
          </p>
        </div>
      </div>

      {overflowing ? (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="flex w-full items-center justify-center px-2 font-body text-body-3 text-text-tertiary transition-colors hover:text-text-primary"
        >
          {expanded ? "Show less" : "View more"}
        </button>
      ) : null}
    </div>
  );
}
